import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { sellerService } from '@/services/seller';
import { useSellerStore } from '@/store/seller';
import { SellerPageContainer } from '@/components/seller/layout/SellerPageContainer';
import type { OrderWithItems } from '@/store/seller';

export function OrderRefundReasonPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const { updateOrderStatus } = useSellerStore();

  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(true);

  useEffect(() => {
    const loadOrder = async () => {
      if (!orderId) return;

      try {
        const result = await sellerService.getOrderDetail(orderId);
        if (result.success && result.data) {
          setOrder(result.data);
        }
      } catch (error) {
        console.error('Order load error:', error);
      } finally {
        setLoadingOrder(false);
      }
    };

    loadOrder();
  }, [orderId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      showToast('환불 사유를 입력해주세요');
      return;
    }

    if (!orderId) {
      showToast('주문 정보를 찾을 수 없습니다');
      return;
    }

    setLoading(true);
    try {
      // sellerService.cancelOrder 사용 (토스페이먼츠 API 연동 포함)
      const result = await sellerService.cancelOrder(orderId, reason.trim());

      if (result.success) {
        // 스토어 상태 업데이트
        updateOrderStatus(orderId, 'CANCELLED');
        
        showToast('환불이 처리되었습니다');
        navigate('/seller/home');
      } else {
        showToast(result.error || '환불 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('Order refund error:', error);
      showToast('환불 처리 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/seller/orders/${orderId}`);
  };

  if (loadingOrder) {
    return (
      <SellerPageContainer activeNavId="home">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">주문 정보를 불러오는 중...</p>
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  return (
    <SellerPageContainer activeNavId="home">
      <div className="pb-4">
        {/* 헤더 */}
        <div className="flex items-center mb-6">
          <button
            onClick={handleCancel}
            className="mr-4 p-2 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-gray-800">주문 환불</h1>
        </div>

        {/* 안내 메시지 */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 text-sm">
            환불 사유를 입력하고 환불 금액을 확인해주세요. 
            환불 처리 후에는 취소가 불가능합니다.
          </p>
        </div>

        {/* 환불 금액 */}
        {order && (
          <div className="bg-white rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-700 mb-3">환불 금액</h3>
            <div className="flex justify-between items-center text-lg">
              <span className="text-gray-600">총 주문 금액</span>
              <span className="font-bold text-red-600">
                {formatCurrency(order.total_amount)}원
              </span>
            </div>
          </div>
        )}

        {/* 사유 입력 */}
        <div className="bg-white rounded-lg p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            환불 사유 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="환불 사유를 입력해주세요"
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            maxLength={500}
          />
          <div className="text-right text-sm text-gray-500 mt-1">
            {reason.length}/500
          </div>
        </div>

        {/* 액션 버튼 */}
        <button
          onClick={handleSubmit}
          disabled={!reason.trim() || loading}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
            reason.trim() && !loading
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {loading ? '처리 중...' : '환불하기'}
        </button>
      </div>

      <ToastContainer />
    </SellerPageContainer>
  );
}